import { useState } from 'react';

import { DropdownMenu } from 'cpool-components/input/DropdownMenu';
import type { APPLICANT_ATTRIBUTION_BOTH_TYPE } from 'cpool-constants';
import { AGGREGATE_NAMES, APPLICANT_ATTRIBUTION_BOTH, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';

import { useRefreshRecordedDocuments } from '../useRefreshRecordedDocuments';
import { APICommunicator } from '~/api/APICommunicator';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import useLead from '~/features/leadDetails/hooks/useLead';
import useCallbackWithErrorHandler from '~/hooks/useCallbackWithErrorHandler';

type Props = {
    attribution: APPLICANT_ATTRIBUTION_BOTH_TYPE;
    documentKey: string;
    leadId: string;
};

export const AttributionCell: React.FC<Props> = ({ attribution, documentKey, leadId }) => {
    const { t } = useTranslation();
    const { data: lead } = useLead(leadId);
    const refreshRecordedDocuments = useRefreshRecordedDocuments(leadId);
    const [isPending, setIsPending] = useState(false);
    const { permissions } = useAclPermissions({ loanRequestId: leadId });

    const isDisabled =
        !permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].changeDocumentAttribution ||
        lead?.antragsteller2?.dataProtectionAgreed === false ||
        isPending;

    const applicantLabel = (value: APPLICANT_ATTRIBUTION_BOTH_TYPE) => {
        const personendaten =
            value === APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1
                ? lead?.antragsteller1?.personendaten
                : lead?.antragsteller2?.personendaten;

        if (personendaten?.vorname && personendaten?.nachname)
            return `${personendaten.vorname} ${personendaten.nachname}`;
        return t(`DOCUMENT_DEMANDER.ATTRIBUTION.${value}`);
    };

    const options = [
        {
            label: applicantLabel(APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1),
            value: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
        },
        {
            label: applicantLabel(APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_2),
            value: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_2,
        },
        { label: t('DOCUMENT_DEMANDER.ATTRIBUTION.BEIDE'), value: APPLICANT_ATTRIBUTION_BOTH.BEIDE },
    ];

    const onChange = useCallbackWithErrorHandler(
        async (value: string | number | Array<string | number>) => {
            const nextAttribution = value as APPLICANT_ATTRIBUTION_BOTH_TYPE;
            if (nextAttribution === attribution) return;

            setIsPending(true);
            try {
                const { position } = await APICommunicator.sendCommand({
                    aggregateId: leadId,
                    aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                    commandName: LOAN_REQUEST_COMMANDS.changeDocumentAttribution,
                    payload: { attribution: nextAttribution, key: documentKey },
                });
                await refreshRecordedDocuments(position);
            } finally {
                setIsPending(false);
            }
        },
        [attribution, documentKey, leadId, refreshRecordedDocuments],
        t('LEAD_DETAILS_GENERAL.ERROR_SAVING_DATA'),
    );

    return (
        <DropdownMenu
            disabled={isDisabled}
            onChange={onChange}
            options={options}
            showBorder={false}
            value={attribution}
        />
    );
};
